import * as React from "react";
import { useLedgerExpectedMatrixRange } from "@/hooks/finance/reconciliation/useLedgerExpected";
import { useRealClosingMatrixRange } from "@/hooks/finance/reconciliation/useRealClosing";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
};

export type LedgerGapRow = {
  ledger_gap: number | null;

  real_closing: number | null;
  real_closing_ts: string | null;

  ledger_expected_closing: number | null;
  currency: string | null;
};

export type LedgerGapMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, LedgerGapRow>>;
};

export function useLedgerGapMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo } = args;

  const ledgerExpectedQ = useLedgerExpectedMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
  });

  const realClosingQ = useRealClosingMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
  });

  const data = React.useMemo<LedgerGapMatrix | undefined>(() => {
    if (!ledgerExpectedQ.data || !realClosingQ.data) return undefined;

    const dateKeys = ledgerExpectedQ.data.dateKeys;
    const byAccount = new Map<string, Map<string, LedgerGapRow>>();

    for (const accId of bankAccountIds) {
      const rowMap = new Map<string, LedgerGapRow>();

      const expMap = ledgerExpectedQ.data.byAccount.get(accId);
      const closeMap = realClosingQ.data.byAccount.get(accId);

      for (const dayKey of dateKeys) {
        const exp = expMap?.get(dayKey);
        const close = closeMap?.get(dayKey);

        const ledger_expected_closing = exp?.ledger_expected_closing ?? null;
        const real_closing = close?.real_closing ?? null;

        const ledger_gap =
          ledger_expected_closing == null || real_closing == null
            ? null
            : real_closing - ledger_expected_closing;

        rowMap.set(dayKey, {
          ledger_gap,
          real_closing,
          real_closing_ts: close?.real_closing_ts ?? null,
          ledger_expected_closing,
          currency: exp?.currency ?? close?.currency ?? null,
        });
      }

      byAccount.set(accId, rowMap);
    }

    return { dateKeys, byAccount };
  }, [ledgerExpectedQ.data, realClosingQ.data, bankAccountIds]);

  return {
    data,
    isLoading: ledgerExpectedQ.isLoading || realClosingQ.isLoading,
    isError: ledgerExpectedQ.isError || realClosingQ.isError,
    error: ledgerExpectedQ.error ?? realClosingQ.error ?? null,
    refetch: async () => {
      await Promise.all([ledgerExpectedQ.refetch(), realClosingQ.refetch()]);
    },
  };
}